import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Loader2, Check, Navigation, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useGeo } from "@/context/LocationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TYPES = [
  { v: "customer", label: "Customer" },
  { v: "provider", label: "Service Provider" },
  { v: "both", label: "Both" },
];

export default function WaitlistForm({
  source = "hero",
  showType = true,
  showZip = true,
  cta = "Join the Waitlist",
  successMsg = "You're in. We'll let you know when SOSConnect AI launches near you.",
  dark = false,
  onSuccess,
}) {
  const geo = useGeo();
  const [form, setForm] = useState({ email: "", city: "", state: "", zip_code: "", user_type: "customer" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [company, setCompany] = useState(""); // honeypot
  const [cityStatus, setCityStatus] = useState(null); // { count, requested } for the current city
  const filledFromGeo = useRef(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Pre-fill city/state from the shared (auto-detected) location, once, if the user hasn't typed one.
  useEffect(() => {
    if (geo?.detected && !filledFromGeo.current && !form.city) {
      filledFromGeo.current = true;
      setForm((f) => ({ ...f, city: geo.city || f.city, state: geo.state || f.state }));
    }
  }, [geo?.detected, geo?.city, geo?.state, form.city]);

  // Check whether the entered city has been requested before (for the "be the first" nudge).
  useEffect(() => {
    const city = form.city.trim();
    if (!city) { setCityStatus(null); return; }
    const id = setTimeout(() => {
      api.get("/cities/status", { params: { city, state: form.state.trim() } })
        .then((r) => setCityStatus(r.data)).catch(() => setCityStatus(null));
    }, 400);
    return () => clearTimeout(id);
  }, [form.city, form.state]);

  const useLocation = () => {
    geo.requestLocation({
      onUnsupported: () => toast.error("Location isn't supported on this device."),
      onDenied: () => toast.message("No problem — just enter your city and state."),
      onError: () => toast.error("Couldn't detect your city. Please enter it manually."),
      onDetected: ({ city, state }) => setForm((f) => ({ ...f, city: city || f.city, state: state || f.state })),
    });
  };

  const locating = geo?.detecting;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.city || !form.state) { toast.error("Please add your email, city and state."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/waitlist", {
        ...form, lat: geo?.lat ?? null, lng: geo?.lng ?? null, source, company,
      });
      setDone(true);
      onSuccess?.();
      if (data.duplicate) toast.success(data.message);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const inputCls = dark
    ? "h-12 rounded-xl border-white/10 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-brand"
    : "h-12 rounded-xl border-black/10 bg-white placeholder:text-neutral-400 focus-visible:ring-brand";

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`flex flex-col items-center gap-4 rounded-3xl p-10 text-center ${dark ? "bg-white/5" : "bg-brand/5"}`}
        data-testid="waitlist-success"
      >
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
          className="relative grid h-16 w-16 place-items-center rounded-full bg-ai text-white">
          <span className="pulse-ring absolute inset-0 rounded-full bg-ai/50" />
          <Check className="h-8 w-8" strokeWidth={3} />
        </motion.div>
        <p className={`max-w-sm text-lg font-semibold ${dark ? "text-white" : "text-neutral-900"}`}>{successMsg}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`waitlist-form-${source}`}>
      <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />

      <button type="button" onClick={useLocation} disabled={locating}
        className={`flex w-full items-center justify-center gap-2 rounded-xl border border-dashed py-2.5 text-sm font-medium transition-colors ${dark ? "border-white/15 text-white/80 hover:bg-white/5" : "border-brand/30 text-brand hover:bg-brand/5"}`}
        data-testid="use-location-btn">
        {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
        {locating ? "Detecting…" : "Use My Location"}
      </button>

      <AnimatePresence>
        {geo?.detected && form.city && (
          <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
            className={`flex items-center gap-1.5 px-1 text-xs ${dark ? "text-white/70" : "text-neutral-500"}`}>
            <MapPin className="h-3.5 w-3.5 text-ai" />
            Looks like you&apos;re in {form.city}, {form.state}. Edit below if that&apos;s not right.
          </motion.p>
        )}
      </AnimatePresence>

      <Input type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} data-testid="waitlist-email" />
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="City" value={form.city} onChange={set("city")} className={inputCls} data-testid="waitlist-city" />
        <Input placeholder="State" value={form.state} onChange={set("state")} className={inputCls} data-testid="waitlist-state" />
      </div>

      <AnimatePresence>
        {cityStatus && !cityStatus.requested && form.city.trim() && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs font-medium ${dark ? "bg-brand/15 text-white" : "bg-brand/5 text-brand"}`}
            data-testid="be-the-first-note">
            <Sparkles className="h-4 w-4 shrink-0" />
            Be the first to request {form.city.trim()}! New cities help put your area on our launch map.
          </motion.div>
        )}
      </AnimatePresence>
      {showZip && <Input placeholder="ZIP code (optional)" value={form.zip_code} onChange={set("zip_code")} className={inputCls} data-testid="waitlist-zip" />}

      {showType && (
        <div>
          <p className={`mb-2 px-1 text-xs font-medium ${dark ? "text-white/60" : "text-neutral-500"}`}>How would you use SOSConnect AI?</p>
          <div className="grid grid-cols-3 gap-2">
            {TYPES.map((t) => (
              <button key={t.v} type="button" onClick={() => setForm((f) => ({ ...f, user_type: t.v }))}
                className={`rounded-xl border py-2.5 text-xs font-semibold transition-colors ${form.user_type === t.v
                  ? "border-brand bg-brand text-white"
                  : dark ? "border-white/10 text-white/70 hover:border-white/25" : "border-black/10 text-neutral-600 hover:border-brand/40"}`}
                data-testid={`waitlist-type-${t.v}`}>{t.label}</button>
            ))}
          </div>
        </div>
      )}

      <Button type="submit" disabled={loading}
        className="h-13 w-full rounded-xl bg-brand py-3.5 text-base font-semibold text-white transition-transform hover:bg-brand-dark active:scale-[0.98]"
        data-testid="waitlist-submit">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : cta}
      </Button>
    </form>
  );
}
